import { combineReducers } from 'redux';
import { call, put, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { removeDoubleQuotes } from 'helpers/downloadHandler';
import {
  AppAction,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { getKahlActions } from 'saga/kahl/ducks';

export interface DownloadEBillDocumentPayload {
  invoiceNumber: string;
  serviceId?: string;
  serviceNo?: string;
  claimId?: string;
  format?: string;
  type?: string;
  openInNewTab?: boolean;
}
/*STATE*/
export interface DownloadEBillDocumentState {
  downloadLink: any;
}
/* ACTION TYPES */
export enum DownloadEBillDocumentActionTypes {
  DownloadEBillDocument = '@@EBillDocument/DOWNLOAD_EBILL_DOCUMENT',
  SetDownloadLink = '@@EBillDocument/SET_DOWNLOAD_LINK',
}

/* ACTIONS */
export const DownloadEBillDocumentActions = createApiActionCreators(
  DownloadEBillDocumentActionTypes.DownloadEBillDocument
);
export const SetDownloadLinkAction = createApiActionCreators(DownloadEBillDocumentActionTypes.SetDownloadLink);

/*REDUCERS*/
const initialState: DownloadEBillDocumentState = {
  downloadLink: null,
};

const downloadLink = createReducer(initialState.downloadLink, {
  [DownloadEBillDocumentActionTypes.SetDownloadLink]: {
    [RequestActionTypes.SUCCESS]: (state: DownloadEBillDocumentState, payload: HTMLAnchorElement) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

export default combineReducers<DownloadEBillDocumentState>({
  downloadLink,
});

/* SELECTORS */
const selectEbillDownload = (state: AppState) => state.downloadEbillDocument;

export const selectDownloadLink = (state: AppState) => selectEbillDownload(state).downloadLink;

/* SAGAS */
function* downloadEBillDocument({ payload }: AppAction<DownloadEBillDocumentPayload>) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.downloadEBillDocument,
      yield getApiUrl(environment.urlConfig.eBillDownloader),
      payload.invoiceNumber,
      payload.serviceId,
      payload.serviceNo,
      payload.claimId,
      payload.format,
      payload.type
    ),
  };

  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const link = document.createElement('a');
    let contentDispositionString = 'Content-Disposition';

    if (!resp.headers[contentDispositionString]) {
      contentDispositionString = 'content-disposition';
    }

    const [, filename] = resp.headers[contentDispositionString].split('filename=');

    link.target = '_blank';
    link.href = URL.createObjectURL(new Blob([resp.data]));
    yield put(SetDownloadLinkAction.success(link));
    link.download = removeDoubleQuotes(decodeURIComponent(filename));
    link.click();
    link.remove();

    if (payload.openInNewTab) {
      window.open(URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
    }
  } else {
    yield put(DownloadEBillDocumentActions.failure());

    if (resp.status === 404) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID0901' }));
    }
  }
}

/* EXPORT */
export function* downloadEBillDocumentSaga() {
  yield takeLatest(
    createActionType(DownloadEBillDocumentActionTypes.DownloadEBillDocument, RequestActionTypes.REQUEST),
    downloadEBillDocument
  );
}
